<template>
  <div class="w-full">
    <!-- Pending State -->
    <div
      v-if="application.match_status === 'PENDING'"
      class="flex items-center justify-center p-6 bg-gray-50 rounded-lg border border-gray-200 gap-2"
    >
      <Loader2 class="w-4 h-4 animate-spin text-cosmicPurple" />
      <p class="text-sm text-gray-600 font-medium">Resume assessment pending...</p>
    </div>

    <!-- Failed State (only visible to guides) -->
    <div
      v-if="application.match_status === 'FAILED' && isGuide"
      class="p-4 bg-red-50 rounded-lg border border-red-200"
    >
      <div class="flex items-start">
        <AlertCircle class="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
        <div class="flex-1">
          <h4 class="text-sm font-medium text-red-800">Match Assessment Failed</h4>
          <p class="text-xs text-red-600 mt-1">
            {{ application.match_error_message || 'An unknown error occurred during assessment.' }}
          </p>
          <button
            @click="resetAssessment"
            :disabled="isResetting"
            class="mt-3 inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Loader2 v-if="isResetting" class="w-3 h-3 mr-1.5 animate-spin" />
            <RotateCcw v-else class="w-3 h-3 mr-1.5" />
            Reset assessment results
          </button>
        </div>
      </div>
    </div>

    <!-- Completed State -->
    <div
      v-else-if="application.match_status === 'COMPLETED'"
      class="bg-white rounded-lg border border-gray-200 overflow-hidden"
    >
      <!-- Header with Score -->
      <div class="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
        <h3 class="font-semibold text-gray-800 flex items-center">
          <Sparkles class="w-4 h-4 text-cosmicPurple mr-2" />
          Match Assessment
        </h3>
        <div class="flex items-center">
          <div class="text-xs text-gray-500 mr-2">Match Score:</div>
          <div
            class="px-3 py-1 rounded-full text-sm font-bold"
            :class="getScoreColorClass(application.match_score)"
          >
            {{ application.match_score }}/10
          </div>
        </div>
      </div>

      <div class="p-4 space-y-4">
        <!-- Referral Status Message -->
        <div
          v-if="application.match_score !== null && application.match_score !== undefined"
          class="p-3 rounded-md border text-sm"
          :class="
            application.match_score >= matchThreshold
              ? 'bg-green-50 border-green-100 text-green-800'
              : 'bg-amber-50 border-amber-100 text-amber-800'
          "
        >
          <div class="flex items-start">
            <CheckCircle2
              v-if="application.match_score >= matchThreshold"
              class="w-4 h-4 mt-0.5 mr-2 flex-shrink-0 text-green-600"
            />
            <AlertCircle v-else class="w-4 h-4 mt-0.5 mr-2 flex-shrink-0 text-amber-600" />
            <div>
              <p v-if="application.match_score >= matchThreshold">
                <strong>High Match:</strong> The team will process your referral request as soon as
                possible.
              </p>
              <p
                v-else-if="
                  application.open_role_details?.employer_details?.resume_match_threshold &&
                  application.match_score <
                    application.open_role_details?.employer_details?.resume_match_threshold
                "
              >
                <strong>Low Match.</strong>
                Referral request cannot be processed, please try a different role.
                <br />
                <span
                  ><strong
                    >{{ application.open_role_details?.employer_details?.name }} threshold:</strong
                  >
                  {{ matchThreshold }}/10</span
                >
              </p>
              <p v-else>
                <strong>Low Match.</strong>
                Referral request cannot be processed, please try a different role.
              </p>
            </div>
          </div>
        </div>

        <!-- Assessment Text -->
        <div v-if="application.match_assessment" class="text-sm text-gray-700 leading-relaxed">
          {{ application.match_assessment }}
        </div>

        <!-- Key Points -->
        <div v-if="application.match_key_points" class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <!-- Alignments -->
          <div
            v-if="application.match_key_points.key_alignments?.length"
            class="bg-green-50/50 rounded-md p-3 border border-green-100"
          >
            <h4 class="text-xs font-semibold text-green-800 mb-2 flex items-center">
              <CheckCircle2 class="w-3.5 h-3.5 mr-1.5" />
              Key Alignments
            </h4>
            <ul class="space-y-2">
              <li
                v-for="(point, index) in application.match_key_points.key_alignments"
                :key="index"
                class="text-xs text-gray-700 flex items-start"
              >
                <span class="mr-2 text-green-500">•</span>
                <span>{{ point }}</span>
              </li>
            </ul>
          </div>

          <!-- Gaps -->
          <div
            v-if="application.match_key_points.key_gaps?.length"
            class="bg-amber-50/50 rounded-md p-3 border border-amber-100"
          >
            <h4 class="text-xs font-semibold text-amber-800 mb-2 flex items-center">
              <AlertTriangle class="w-3.5 h-3.5 mr-1.5" />
              Potential Gaps
            </h4>
            <ul class="space-y-2">
              <li
                v-for="(point, index) in application.match_key_points.key_gaps"
                :key="index"
                class="text-xs text-gray-700 flex items-start"
              >
                <span class="mr-2 text-amber-500">•</span>
                <span>{{ point }}</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Timestamp -->
        <div
          v-if="application.match_given_at"
          class="text-[10px] text-gray-400 text-right pt-2 border-t border-gray-50"
        >
          Assessment generated on {{ formatDate(application.match_given_at) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import {
  Loader2,
  AlertCircle,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  RotateCcw
} from 'lucide-vue-next'
import type { Application } from '@/types/application'
import { formatDate } from '@/utils/formatDate'
import { jobMatchingThreshold } from '@/const/jobMatching'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import { useToast } from '@/components/ui/toast/use-toast'
import { useAuth } from '@/composables/useAuth'

const { toast } = useToast()
const { isGuide } = useAuth()

const props = defineProps<{
  application: Application
}>()

const emit = defineEmits<{
  (e: 'update', application: Application): void
}>()

const matchThreshold = computed(() => {
  return (
    props.application.open_role_details?.employer_details?.resume_match_threshold ??
    jobMatchingThreshold
  )
})

const isResetting = ref(false)

const resetAssessment = async () => {
  if (isResetting.value) return

  isResetting.value = true
  try {
    await api.patch(API_ROUTES.UPDATE_USER_APPLICATION(String(props.application.id)), {
      match_score: null,
      match_assessment: null,
      match_key_points: {},
      match_given_at: null,
      match_status: null,
      match_error_message: null
    })
    // Fetch the updated application data
    const response = await api.get<Application>(
      API_ROUTES.GET_USER_APPLICATION_BY_ID(String(props.application.id))
    )
    emit('update', response.data)

    toast({
      title: 'Assessment reset',
      description: 'Match assessment results have been successfully reset.'
    })
  } catch (error) {
    console.error('Failed to reset assessment:', error)

    toast({
      title: 'Reset failed',
      description: 'Failed to reset assessment results. Please try again.',
      variant: 'destructive'
    })
  } finally {
    isResetting.value = false
  }
}

const getScoreColorClass = (score: number | null | undefined) => {
  if (score === null || score === undefined) return 'bg-gray-100 text-gray-600'
  if (score >= matchThreshold.value) return 'bg-green-100 text-green-700'
  if (score < matchThreshold.value) return 'bg-yellow-100 text-yellow-700'
  return 'bg-red-100 text-red-700'
}
</script>
